import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { InfoIcon } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { useInstitutionOnboardingNextDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingNextDetailsRoute';
import { useInstitutionOnboardingPrevDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingPrevDetailsRoute';
import { useInstitutionTaxSelfCertificationRedirect } from '@/features/institution/hooks/useInstitutionTaxSelfCertificationRedirect';
import {
  createInstitutionProprietorTaxSelfCertificationDefaults,
  getInstitutionProprietor,
  type InstitutionProprietorTaxSelfCertification,
  institutionProprietorTaxSelfCertificationSchema,
  institutionProprietorTaxSelfCertificationToInstitutionDetailsProprietorTaxSelfCertificationApi,
} from '@/features/institution/proprietor-tax-self-certification/schema';
import { getDetailsKeyProgress, getV2OnboardingDetailsKeyRoute, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import type { FormStep } from '@/types/components';
import { InstitutionProprietorTaxSelfCertificationFieldset, InstitutionProprietorTaxSelfCertificationFieldsetLegend } from './fieldset';

const FORM_STEPS = {
  DECLARATION: {
    Component: InstitutionProprietorTaxSelfCertificationFieldset,
    Legend: InstitutionProprietorTaxSelfCertificationFieldsetLegend,
    schema: institutionProprietorTaxSelfCertificationSchema,
  },
} satisfies Record<string, FormStep<InstitutionProprietorTaxSelfCertification>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionProprietorTaxSelfCertificationFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionProprietorTaxSelfCertification>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'proprietorTaxSelfCertification', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <Step />
    </PageCardContent>
  );
};
InstitutionProprietorTaxSelfCertificationFormCurrentStep.displayName = 'InstitutionProprietorTaxSelfCertificationFormCurrentStep';

const InstitutionProprietorTaxSelfCertificationFormBackButton = () => {
  const navigate = useNavigate();
  const prevDetailsRoute = useInstitutionOnboardingPrevDetailsRoute('proprietorTaxSelfCertification');

  const handlePrevStep = useCallback(() => {
    navigate(prevDetailsRoute, { replace: true });
  }, [navigate, prevDetailsRoute]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionProprietorTaxSelfCertificationFormBackButton.displayName = 'InstitutionProprietorTaxSelfCertificationFormBackButton';

const InstitutionProprietorTaxSelfCertificationFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { nextStep } = useGuardedFormStepper<InstitutionProprietorTaxSelfCertification>();
  const { t } = useTranslation();

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionProprietorTaxSelfCertificationFormFooter.displayName = 'InstitutionProprietorTaxSelfCertificationFormFooter';

// Without a sole proprietor in the ownership section, there is no one to sign and no residences to certify
const InstitutionProprietorTaxSelfCertificationNoProprietor = () => {
  const onboardingPath = useInstitutionOnboardingBasePath();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <fieldset>
      <InstitutionProprietorTaxSelfCertificationFieldsetLegend />
      <InstitutionProprietorTaxSelfCertificationFormBackButton />
      <PageCard size="lg" bordered fixedFooter>
        <PageCardContent className="pt-8">
          <Alert variant="info" role="status">
            <InfoIcon aria-hidden className="size-4" />
            <AlertDescription>{t('institutionDetails.proprietorTaxSelfCertification.noProprietor')}</AlertDescription>
          </Alert>
        </PageCardContent>
        <PageCardFooter>
          <Button className="w-full" size="lg" variant="primary" onClick={() => navigate(getV2OnboardingDetailsKeyRoute(onboardingPath, 'ownership'))}>
            {t('institutionDetails.proprietorTaxSelfCertification.goToOwnership')}
          </Button>
        </PageCardFooter>
      </PageCard>
    </fieldset>
  );
};
InstitutionProprietorTaxSelfCertificationNoProprietor.displayName = 'InstitutionProprietorTaxSelfCertificationNoProprietor';

const InstitutionProprietorTaxSelfCertificationForm = () => {
  const onboardingPath = useInstitutionOnboardingBasePath();
  const navigate = useNavigate();
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'proprietorTaxSelfCertification');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const getNextDetailsRoute = useInstitutionOnboardingNextDetailsRoute('proprietorTaxSelfCertification');
  const isRedirecting = useInstitutionTaxSelfCertificationRedirect('proprietorTaxSelfCertification');
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow || isRedirecting;
  const proprietor = getInstitutionProprietor(institutionDetailsAnswers?.ownership);

  if (isLoading) return <LoadingCard />;

  if (!proprietor) return <InstitutionProprietorTaxSelfCertificationNoProprietor />;

  const onSubmit = async (data: InstitutionProprietorTaxSelfCertification, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    try {
      const savedAnswers = {
        proprietorTaxSelfCertification: await institutionProprietorTaxSelfCertificationToInstitutionDetailsProprietorTaxSelfCertificationApi(data, proprietor),
      };

      await mutateAsync(savedAnswers);
      navigate(isReturningToReview ? `${onboardingPath}/review` : getNextDetailsRoute(savedAnswers), { replace: true });
    } catch {
      toast({
        description: t('fundamentals.defaultError.message'),
        variant: 'destructive',
      });
    }
  };

  const defaultValues = createInstitutionProprietorTaxSelfCertificationDefaults(institutionDetailsAnswers?.proprietorTaxSelfCertification, proprietor);

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionProprietorTaxSelfCertificationFieldsetLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionProprietorTaxSelfCertificationFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionProprietorTaxSelfCertification) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionProprietorTaxSelfCertificationFormCurrentStep />
          <InstitutionProprietorTaxSelfCertificationFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionProprietorTaxSelfCertificationForm.displayName = 'InstitutionProprietorTaxSelfCertificationForm';

export default InstitutionProprietorTaxSelfCertificationForm;
